import { DatabaseService, PricingService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DatabaseAttributes, DatabaseBulkUpdateParams, DatabaseInstanceTypeResult,
    DatabaseValidation, OutputRDS, RDSInstanceTypes } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Autosuggest, AutosuggestProps, Box, Button, ExpandableSection, 
    Grid, Header, NonCancelableCustomEvent, Select, SelectProps, SpaceBetween } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../../common/Localization';
import AWSCATTable from '../../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../../common/table/types';
import { ErrorWithResponse, MatchParam } from '../../../../common/types';
import { TcoContext } from '../../../../contexts/TcoContext';
import { overrideFieldOptions } from '../../common/Common';
import { updateTCOAssessment } from '../../TCOHelper';

type Props = {
    outputRDS: OutputRDS;
    rdsAssessmentId: string;
} & FlashContextInterface;

const pricingService = new PricingService();
const tcoService = new TcoService();
const databaseService = new DatabaseService();

function RDSInstances({ outputRDS, rdsAssessmentId, flash }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;
    
    const dbInstances: DatabaseInstanceTypeResult[] = outputRDS?.instanceTypeResults;

    const isMounted = useIsMounted();
    const { assessment, updateAssessment, updateTcoLoading } = useContext(TcoContext);

    const [ bulkUpdateLoading, setBulkUpdateLoading ] = useState(false);
    const [ bulkUpdateSelectedField, setBulkUpdateSelectedField ] = useState<SelectProps.Option | null>(null);
    const [ bulkUpdateSelectedInstance, setBulkUpdateSelectedInstance ] = useState<string>('');
    const [ selectedDatabases, setSelectedDatabases ] = useState<DatabaseInstanceTypeResult[]>([]);
    const [ columnDefinitions, setColumnDefinitions ] = useState<ColumnDefinition<DatabaseInstanceTypeResult>[]>([]);
    const [ rdsInstanceTypesByRegion, setRdsInstanceTypesByRegion ] = useState<RDSInstanceTypes>();
    
    const loadInstanceOptions = async () => {
        try {
            setBulkUpdateLoading(true);
            const instances = await pricingService.getRDSInstanceTypes();
            if (isMounted()) {
                setRdsInstanceTypesByRegion(instances);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_RDS_INSTANCE_TYPES);
            }
        } finally {
            if (isMounted()) {
                setBulkUpdateLoading(false);
            }
        }
    };

    useEffect(() => {
        loadInstanceOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setColumns = () => {
        const columnDefs: ColumnDefinition<DatabaseInstanceTypeResult>[] = [
            {
                id: 'databaseId',
                header: constants.DATABASE_ID,
                type: ColumnTypes.TEXT,
                cell: item => item.databaseId,
            }, 
            {
                id: 'average',
                header: constants.AVERAGE_UTILIZATION,
                type: ColumnTypes.TEXT,
                cell: item => item.average,
            },
            {
                id: 'peak',
                header: constants.PEAK_UTILIZATION,
                type: ColumnTypes.TEXT,
                cell: item => item.peak,
            },
            {
                id: 'specification',
                header: constants.ON_PREM_CAPACITY,
                type: ColumnTypes.TEXT,
                cell: item => item.specification,
            },
            {
                id: 'override',
                header: constants.OVERRIDDEN,
                type: ColumnTypes.BOOLEAN,
                data: item => item.override ? constants.TRUE : constants.FALSE,
                cell: item => item.override ? constants.TRUE : constants.FALSE,
            }
        ];
        if (isMounted()) {
            setColumnDefinitions(columnDefs);
        }
    };

    useEffect(() => {
        setColumns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ dbInstances ]);

    const getAutoSuggestOptions = (): AutosuggestProps.Option[] => {
        const options = [
            {value: constants.UNDO_MANUAL_OVERRIDE},
        ];

        if (assessment && rdsInstanceTypesByRegion?.[assessment.configuration.tcoAssumptions.awsRegion as keyof RDSInstanceTypes]) {
            rdsInstanceTypesByRegion[assessment.configuration.tcoAssumptions.awsRegion as keyof RDSInstanceTypes].forEach(instance => {
                options.push({value: instance});
            });
        }

        return options;
    };

    const onFieldUpdate = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        setBulkUpdateSelectedField(event.detail.selectedOption);
    };

    const onInstanceSelectionChange = (event: NonCancelableCustomEvent<AutosuggestProps.ChangeDetail>) => {
        setBulkUpdateSelectedInstance(event.detail.value);
    };

    const onTableSelectionUpdate = (items: readonly DatabaseInstanceTypeResult[] | undefined) => {
        setSelectedDatabases(items ? [...items] : []);
    };

    const onUpdateClick = async () => {
        try {
            setBulkUpdateLoading(true);
            updateTcoLoading(true);
            const databaseIds: string[] = selectedDatabases.map(database => database.databaseId);
            if (!databaseIds.length || !bulkUpdateSelectedField || !bulkUpdateSelectedInstance) {
                flash.alert(FlashType.error, constants.ERROR_OVERRIDING_RDS_RECOMMENDATION, constants.INVALID_INPUT);
                return;
            }
            
            if (portfolioId && assessment) {
                const conditions: DatabaseBulkUpdateParams = {
                    portfolioId,
                    databaseIds,
                    fieldName: `rdsRecommendationOverride${bulkUpdateSelectedField.value}`,
                    fieldValue: bulkUpdateSelectedInstance,
                };

                if (bulkUpdateSelectedInstance === constants.UNDO_MANUAL_OVERRIDE) {
                    conditions.fieldValue = null;
                }

                const databaseAttributesMap = outputRDS.databases.reduce((map, database) => {
                    map.set(database.databaseId, database.instanceAttributes);
                    return map;
                }, new Map<string, DatabaseAttributes>());
                const validations: DatabaseValidation[] = [];
                if (conditions.fieldValue) {
                    conditions.databaseIds.forEach(databaseId => {
                        const attributes = databaseAttributesMap.get(databaseId);
                        if (attributes) {
                            validations.push({
                                databaseId,
                                dbEngine: attributes.databaseEngine,
                                dbEdition: attributes.databaseEdition,
                                deploymentOption: attributes?.deploymentOption,
                                licenseModel: attributes?.licenseModel,
                                region: assessment?.configuration.tcoAssumptions.awsRegion,
                                instanceType: conditions.fieldValue?.toString() ?? '',
                            });
                        }
                    });
                }

                await tcoService.validateOverrideRdsRecommendation(rdsAssessmentId, validations);

                await databaseService.update(conditions);
                
                const updatedAssessment = await updateTCOAssessment(portfolioId, assessment, tcoService);
                if (isMounted()) {
                    updateAssessment(updatedAssessment);
                    flash.alert(FlashType.success, constants.UPDATE_SUCCESSFUL, '');
                }
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, constants.VALIDATION_FAILED);
            }
        } finally {
            setBulkUpdateLoading(false);
            updateTcoLoading(false);
        }
    };
    
    const overrideRecommendation: JSX.Element = (
        <ExpandableSection header={constants.OVERRIDE_RDS_RECOMMENDATION} defaultExpanded={true}>
            <Grid gridDefinition={[{colspan: 4}, {colspan: 4}, {colspan: 4}]}>
                <Select
                    disabled={bulkUpdateLoading}
                    selectedOption={bulkUpdateSelectedField}
                    options={overrideFieldOptions}
                    onChange={onFieldUpdate}
                    placeholder={constants.RECOMMENDATION_TYPE}
                />
                <Autosuggest
                    disabled={bulkUpdateLoading}
                    options={getAutoSuggestOptions()}
                    value={bulkUpdateSelectedInstance}
                    onChange={onInstanceSelectionChange}
                    placeholder={constants.LIST_OF_INSTANCE_TYPE}
                    enteredTextLabel={value => `Use: '${value}'`}
                />
                <Box float='right'>
                    <Button
                        variant='primary'
                        onClick={onUpdateClick}
                        loading={bulkUpdateLoading}
                        disabled={bulkUpdateSelectedInstance === '' || !bulkUpdateSelectedField || !selectedDatabases.length}
                    >
                        {`${constants.UPDATE_BUTTON_TEXT} ${selectedDatabases.length} ${constants.ITEM_S}`}
                    </Button>
                </Box>
            </Grid>
        </ExpandableSection>
    );
    
    return (
        <SpaceBetween size='m'>
            <Header variant='h1'>{constants.DETAILS}</Header>
            {overrideRecommendation}
            <AWSCATTable
                resourceName={constants.AWS_DB_INSTANCE_TYPE_DETAILS}
                items={dbInstances}
                columnDefinitions={columnDefinitions}
                selectionType='multi'
                onSelectionChange={onTableSelectionUpdate}
                autoGenerateVisibleContent
                enableAutoSorting
                useDefaultDownload
            />
        </SpaceBetween>
    );
}

export default withFlashContext(RDSInstances);
